/**
 * The retry rule for the consent page's describe call, kept apart from apiRequest so it can be
 * tested on its own (`npx tsx --test src/features/oauth/__tests__/consentRetry.test.ts`).
 *
 * Only for describe: it only reads the transaction. Approve consumes it and is never retried.
 */

import type { ConsentResult } from "./oauthService"

/** Waits between attempts (10 s of waiting in all, well inside the transaction's 10 minutes). */
export const CONSENT_RETRY_DELAYS_MS = [1_000, 3_000, 6_000]
/**
 * No new attempt starts after this. Each attempt can itself take apiRequest's 15 s timeout (more
 * when its 401 path renews the token), so without a bound a dead network kept the skeleton up for
 * over a minute before the error page.
 */
export const CONSENT_RETRY_BUDGET_MS = 20_000

type ConsentError = Extract<ConsentResult<unknown>, { ok: false }>

/** No answer, a token the API could not accept yet, or a server that could not answer now. */
export function isTransientConsentFailure(failed: ConsentError): boolean {
  const { status } = failed
  return status === undefined || status === 401 || status === 408 || status === 429 || status >= 500
}

export interface ConsentRetryOptions {
  delaysMs?: number[]
  budgetMs?: number
  now?: () => number
  sleep?: (ms: number) => Promise<void>
}

/**
 * Runs `attempt` until it succeeds, says the transaction is gone (404), fails in a way a retry
 * cannot fix (400/403 …), the delays or the time budget run out, or `isCancelled()` turns true.
 * A thrown attempt (apiRequest throws without a usable token or without an answer) counts as
 * "no answer". Never rejects.
 */
export async function retryConsentCall<T>(
  attempt: () => Promise<ConsentResult<T>>,
  isCancelled: () => boolean,
  options: ConsentRetryOptions = {},
): Promise<ConsentResult<T>> {
  const delays = options.delaysMs ?? CONSENT_RETRY_DELAYS_MS
  const budget = options.budgetMs ?? CONSENT_RETRY_BUDGET_MS
  const now = options.now ?? Date.now
  const sleep = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)))
  const startedAt = now()

  for (let index = 0; ; index++) {
    let result: ConsentResult<T>
    try {
      result = await attempt()
    } catch (error) {
      console.warn("[oauth] describe failed without an answer:", error instanceof Error ? error.message : error)
      result = { ok: false, reason: "error" }
    }
    if (result.ok || result.reason === "expired" || !isTransientConsentFailure(result)) return result
    const delay = delays[index]
    if (delay === undefined || isCancelled()) return result
    if (now() - startedAt + delay > budget) return result
    console.warn(`[oauth] describe failed (HTTP ${result.status ?? "none"}, ${result.code ?? "no code"}), retrying`)
    await sleep(delay)
    if (isCancelled()) return result
  }
}
